import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight"
import SkipNextOutlinedIcon from "@mui/icons-material/SkipNextOutlined"
import StopOutlinedIcon from "@mui/icons-material/StopOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Avatar from "@mui/material/Avatar"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import IconButton from "@mui/material/IconButton"
import LinearProgress from "@mui/material/LinearProgress"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { amdSessionSummary, queueAmdChip } from "./amd"
import {
  itemOfficeName,
  leadInitials,
  sessionProgressLabel,
  sessionProgressPercent,
  shouldShowActiveDialerOfficeName,
} from "./powerDialer"

export const POWER_DIALER_SIDEBAR_EXPANDED = 400
export const POWER_DIALER_SIDEBAR_COLLAPSED = 72

const QUEUE_STATUS = {
  completed: { label: "Logged", color: "success" },
  skipped: { label: "Skipped", color: "default" },
  pending: { label: "Waiting", color: "default" },
}

function queueStatusChip(
  lead,
  { isCurrent, wrappingUp, wrapUpSecondsLeft, isBusy }
) {
  if (lead.is_dnc || lead.skip_reason === "dnc") {
    return { label: "Skip — DNC", color: "error" }
  }
  if (isCurrent) {
    if (wrappingUp) {
      return { label: `Next in ${wrapUpSecondsLeft}s`, color: "primary" }
    }
    return { label: isBusy ? "Calling" : "Current", color: "primary" }
  }
  return queueAmdChip(lead) || QUEUE_STATUS[lead.status] || QUEUE_STATUS.pending
}

export default function PowerDialerBar({
  session,
  wrapUpSecondsLeft,
  isBusy,
  isAdvancing,
  showDailyReports = false,
  onSkip,
  onEnd,
  onDailyReports,
}) {
  const [expanded, setExpanded] = useState(true)

  useEffect(() => {
    setExpanded(true)
  }, [session?.id])

  useEffect(() => {
    const width = session
      ? expanded
        ? POWER_DIALER_SIDEBAR_EXPANDED
        : POWER_DIALER_SIDEBAR_COLLAPSED
      : 0
    document.documentElement.style.setProperty(
      "--power-dialer-sidebar-width",
      `${width}px`
    )
    return () => {
      document.documentElement.style.setProperty(
        "--power-dialer-sidebar-width",
        "0px"
      )
    }
  }, [expanded, session])

  if (!session) return null

  const wrappingUp = wrapUpSecondsLeft != null && wrapUpSecondsLeft > 0
  const currentName = session.current_item?.name || "No more leads"
  const currentOfficeName = itemOfficeName(session.current_item)
  const showOfficeName = shouldShowActiveDialerOfficeName({
    isBusy,
    wrappingUp,
    officeName: currentOfficeName,
  })
  const currentId = session.current_item?.lead_ref_id
  const skipDisabled = isBusy || isAdvancing || !session.current_item
  const wrapTotal = Math.max(1, session.wrap_up_seconds || 3)
  const wrapProgress = wrappingUp
    ? Math.max(4, (wrapUpSecondsLeft / wrapTotal) * 100)
    : 0
  const statusLabel = wrappingUp
    ? "Calling next"
    : isBusy
    ? "On a call"
    : "Ready to dial"
  const remaining = Math.max(0, Number(session.remaining_count) || 0)
  const completed = Number(session.completed_count) || 0
  const skipped = Number(session.skipped_count) || 0
  const queueItems = Array.isArray(session.queue_items)
    ? session.queue_items
    : []
  const sessionName = session.name || "Power Dialer"
  const amdSummary = amdSessionSummary(session.amd)

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        right: 0,
        height: "100vh",
        width: {
          xs: expanded ? "100%" : POWER_DIALER_SIDEBAR_COLLAPSED,
          sm: expanded
            ? POWER_DIALER_SIDEBAR_EXPANDED
            : POWER_DIALER_SIDEBAR_COLLAPSED,
        },
        zIndex: 1150,
        display: "flex",
        flexDirection: "column",
        bgcolor: expanded ? "background.paper" : "colors.brand50",
        borderLeft: "1px solid",
        borderColor: "colors.brand200",
        boxShadow: "-8px 0 24px rgba(147, 32, 213, 0.08)",
        overflow: "visible",
        transition: (theme) =>
          theme.transitions.create(["width", "background-color"], {
            duration: theme.transitions.duration.shortest,
          }),
      }}
    >
      <Tooltip
        title={expanded ? "Collapse Power Dialer" : "Expand Power Dialer"}
        placement="left"
      >
        <IconButton
          aria-label={
            expanded ? "Collapse Power Dialer" : "Expand Power Dialer"
          }
          onClick={() => setExpanded((open) => !open)}
          sx={{
            position: "absolute",
            left: -20,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            width: 40,
            height: 40,
            bgcolor: "primary.main",
            color: "common.white",
            boxShadow: 3,
            "&:hover": { bgcolor: "primary.dark" },
          }}
        >
          <KeyboardDoubleArrowRightIcon
            sx={{
              color: "common.white",
              transform: expanded ? "none" : "rotate(180deg)",
              transition: (theme) => theme.transitions.create("transform"),
            }}
          />
        </IconButton>
      </Tooltip>

      {expanded ? (
        <>
          <Box
            sx={{
              px: 2,
              py: 1.75,
              bgcolor: "colors.brand50",
              borderBottom: "1px solid",
              borderColor: "colors.brand200",
              flexShrink: 0,
            }}
          >
            <Row
              sx={{
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Column sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="caption"
                  color="primary.main"
                  sx={{ fontWeight: 700, letterSpacing: "0.08em" }}
                >
                  POWER DIALER
                </Typography>
                <Typography
                  variant="subtitle1"
                  noWrap
                  title={sessionName}
                  sx={{ fontWeight: 800, lineHeight: 1.25 }}
                >
                  {sessionName}
                </Typography>
              </Column>
              {showDailyReports ? (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={onDailyReports}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  Daily Reports
                </Button>
              ) : null}
            </Row>
            {amdSummary ? (
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                title={amdSummary}
                sx={{ fontWeight: 600 }}
              >
                {amdSummary}
              </Typography>
            ) : null}
          </Box>

          <Column
            sx={{
              gap: 1.75,
              p: 2,
              flex: 1,
              minHeight: 0,
              overflow: "auto",
            }}
          >
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "colors.brand50",
                border: "1px solid",
                borderColor: "colors.brand200",
              }}
            >
              <Row
                sx={{ justifyContent: "space-between", alignItems: "center" }}
              >
                <Column>
                  <Typography
                    variant="caption"
                    color="primary.main"
                    sx={{ letterSpacing: "0.08em", fontWeight: 700 }}
                  >
                    SESSION
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.2,
                      color: "text.primary",
                    }}
                  >
                    {sessionProgressLabel(session)}
                  </Typography>
                </Column>
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.4,
                    borderRadius: 999,
                    bgcolor: "colors.brand100",
                    color: "primary.dark",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {remaining} remaining
                </Box>
              </Row>
              <LinearProgress
                variant="determinate"
                value={sessionProgressPercent(session)}
                sx={{
                  mt: 1.25,
                  height: 6,
                  borderRadius: 999,
                  bgcolor: "colors.brand200",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 999,
                    bgcolor: "primary.main",
                  },
                }}
              />
            </Box>

            <Row sx={{ gap: 1 }}>
              <StatCard label="Logged" value={completed} />
              <StatCard label="Remaining" value={remaining} emphasis />
              <StatCard label="Skipped" value={skipped} />
            </Row>

            <Row
              sx={{
                alignItems: "center",
                gap: 1.5,
                p: 1.25,
                borderRadius: 2,
                bgcolor: wrappingUp ? "colors.brand50" : "background.paper",
                border: "1px solid",
                borderColor: wrappingUp ? "colors.brand200" : "colors.gray200",
              }}
            >
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  fontWeight: 700,
                  bgcolor: wrappingUp ? "primary.main" : "colors.brand100",
                  color: wrappingUp ? "common.white" : "primary.dark",
                }}
              >
                {leadInitials(session.current_item?.name)}
              </Avatar>
              <Column sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color={wrappingUp ? "primary.main" : "text.secondary"}
                  sx={{ fontWeight: 700, letterSpacing: "0.04em" }}
                >
                  {statusLabel.toUpperCase()}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.25,
                    color: session.current_item?.is_dnc
                      ? "error.dark"
                      : "inherit",
                  }}
                  noWrap
                  title={currentName}
                >
                  {currentName}
                </Typography>
                {showOfficeName ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    title={currentOfficeName}
                    sx={{ fontWeight: 600 }}
                  >
                    {currentOfficeName}
                  </Typography>
                ) : null}
                {session.current_item?.is_dnc ? (
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ fontWeight: 700 }}
                  >
                    Skip this lead as it&apos;s DNC.
                  </Typography>
                ) : null}
              </Column>
              {wrappingUp ? (
                <WrapUpCountdown
                  secondsLeft={wrapUpSecondsLeft}
                  progress={wrapProgress}
                />
              ) : null}
            </Row>

            <Column sx={{ gap: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: "0.06em" }}
              >
                CALL LIST
              </Typography>
              <Column sx={{ gap: 0.75 }}>
                {queueItems.map((lead, index) => {
                  const isCurrent =
                    String(lead.lead_ref_id) === String(currentId) &&
                    lead.status === "pending"
                  const chip = queueStatusChip(lead, {
                    isCurrent,
                    wrappingUp,
                    wrapUpSecondsLeft,
                    isBusy,
                  })
                  const isDnc = Boolean(
                    lead.is_dnc || lead.skip_reason === "dnc"
                  )
                  return (
                    <Row
                      key={lead.lead_ref_id || `${lead.name}-${index}`}
                      sx={{
                        alignItems: "center",
                        gap: 1,
                        px: 1,
                        py: 0.85,
                        borderRadius: 1.5,
                        bgcolor: isDnc
                          ? "rgba(211, 47, 47, 0.08)"
                          : isCurrent
                          ? "colors.brand50"
                          : "colors.gray50",
                        border: "1px solid",
                        borderColor: isDnc
                          ? "error.light"
                          : isCurrent
                          ? "colors.brand200"
                          : "transparent",
                      }}
                    >
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          display: "grid",
                          placeItems: "center",
                          bgcolor: isCurrent
                            ? "primary.main"
                            : "colors.brand100",
                          color: isCurrent ? "common.white" : "primary.dark",
                          fontSize: 11,
                          fontWeight: 800,
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography
                        variant="body2"
                        noWrap
                        title={
                          isDnc
                            ? `${
                                lead.name || "Unnamed lead"
                              } — skip this lead as it's DNC`
                            : lead.name
                        }
                        sx={{
                          fontWeight: 600,
                          flex: 1,
                          minWidth: 0,
                          color: isDnc ? "error.dark" : "inherit",
                        }}
                      >
                        {lead.name || "Unnamed lead"}
                      </Typography>
                      <Chip
                        size="small"
                        color={chip.color}
                        label={chip.label}
                        sx={{ fontWeight: 700 }}
                      />
                    </Row>
                  )
                })}
              </Column>
            </Column>
          </Column>

          <Row
            sx={{
              gap: 1,
              px: 2,
              py: 1.5,
              borderTop: "1px solid",
              borderColor: "colors.gray200",
              bgcolor: "background.paper",
              flexShrink: 0,
            }}
          >
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<StopOutlinedIcon />}
              onClick={onEnd}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              End session
            </Button>
            <LoadingButton
              size="small"
              variant="outlined"
              startIcon={<SkipNextOutlinedIcon />}
              loading={isAdvancing}
              disabled={skipDisabled}
              onClick={onSkip}
              sx={{ textTransform: "none", fontWeight: 700, ml: "auto" }}
            >
              Skip
            </LoadingButton>
          </Row>
        </>
      ) : (
        <CollapsedDialerRail
          sessionName={sessionName}
          currentName={currentName}
          statusLabel={statusLabel}
          wrappingUp={wrappingUp}
          wrapUpSecondsLeft={wrapUpSecondsLeft}
          progressLabel={sessionProgressLabel(session)}
          onExpand={() => setExpanded(true)}
        />
      )}
    </Box>
  )
}

function CollapsedDialerRail({
  sessionName,
  currentName,
  statusLabel,
  wrappingUp,
  wrapUpSecondsLeft,
  progressLabel,
  onExpand,
}) {
  return (
    <Column
      onClick={onExpand}
      sx={{
        alignItems: "center",
        py: 2,
        px: 0.75,
        gap: 1.5,
        height: "100%",
        cursor: "pointer",
      }}
    >
      <Avatar
        sx={{
          width: 40,
          height: 40,
          fontWeight: 700,
          bgcolor: wrappingUp ? "primary.main" : "primary.light",
          color: "common.white",
        }}
      >
        {leadInitials(currentName)}
      </Avatar>
      <Typography
        title={sessionName}
        sx={{
          writingMode: "vertical-rl",
          transform: "rotate(180deg)",
          fontWeight: 800,
          fontSize: 13,
          letterSpacing: "0.08em",
          color: "primary.main",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxHeight: "40%",
        }}
      >
        {sessionName}
      </Typography>
      <Box sx={{ flex: 1 }} />
      <Typography
        variant="caption"
        sx={{
          writingMode: "vertical-rl",
          transform: "rotate(180deg)",
          fontWeight: 800,
          color: "primary.dark",
        }}
      >
        {wrappingUp ? `Next ${wrapUpSecondsLeft}s` : statusLabel}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          color: "primary.main",
        }}
      >
        {progressLabel}
      </Typography>
    </Column>
  )
}

function StatCard({ label, value, emphasis = false }) {
  return (
    <Column
      sx={{
        flex: 1,
        p: 1,
        borderRadius: 1.5,
        bgcolor: emphasis ? "colors.brand50" : "colors.gray50",
        alignItems: "center",
        border: "1px solid",
        borderColor: emphasis ? "colors.brand200" : "colors.gray200",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 700 }}
      >
        {label}
      </Typography>
    </Column>
  )
}

function WrapUpCountdown({ secondsLeft, progress }) {
  const urgent = secondsLeft <= 1
  return (
    <Box
      sx={{
        position: "relative",
        width: 64,
        height: 64,
        flexShrink: 0,
        "@keyframes powerDialerPulse": {
          "0%, 100%": { transform: "scale(1)" },
          "50%": { transform: "scale(1.06)" },
        },
        animation: "powerDialerPulse 1s ease-in-out infinite",
      }}
    >
      <CircularProgress
        variant="determinate"
        value={100}
        size={64}
        thickness={4}
        sx={{ color: "colors.brand200", position: "absolute", inset: 0 }}
      />
      <CircularProgress
        variant="determinate"
        value={progress}
        size={64}
        thickness={4}
        sx={{
          color: urgent ? "error.main" : "primary.main",
          position: "absolute",
          inset: 0,
        }}
      />
      <Column
        sx={{
          position: "absolute",
          inset: 0,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 20,
            lineHeight: 1,
            color: urgent ? "error.main" : "primary.main",
          }}
        >
          {secondsLeft}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            fontSize: 9,
            letterSpacing: "0.08em",
            color: urgent ? "error.main" : "primary.main",
          }}
        >
          SEC
        </Typography>
      </Column>
    </Box>
  )
}
